import { Button, TextField, Typography, Box, Alert, Select, MenuItem, FormControl, InputLabel, CircularProgress } from '@mui/material';
import { useState, useEffect } from 'react';
import { listResource, getResource, uploadFile, downloadFileByKey, deleteFile, createResource, authenticatedFetch } from '@diggens/api-client';
import { useCreateSubmission } from '@diggens/shared-hooks';
import type { Fund } from '@diggens/types';

interface UploadDocumentFormProps {
  data: {
    file?: {
      name: string;
      size: number;
      type: string;
      temp_storage_path: string;
      bucket: string;
    };
    funds: Fund[];
  };
  tenantId: string;
  onSuccess: (result: { submissionId: string; fundId: string; companyName: string; period: string }) => void;
  onClose: () => void;
}

export function UploadDocumentForm({ data, tenantId, onSuccess, onClose }: UploadDocumentFormProps) {
  console.log('UploadDocumentForm received data:', data);

  if (!data.file) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">File data is missing. Please upload the file again.</Alert>
        <Button onClick={onClose} sx={{ mt: 2 }}>
          Close
        </Button>
      </Box>
    );
  }

  const [fundId, setFundId] = useState('');
  const [portfolioCompanyId, setPortfolioCompanyId] = useState('');
  const [period, setPeriod] = useState('');
  const [companies, setCompanies] = useState<any[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');

  const createSubmission = useCreateSubmission();

  // Load companies when fund is selected
  useEffect(() => {
    if (!fundId) {
      setCompanies([]);
      setPortfolioCompanyId('');
      return;
    }

    const loadCompanies = async () => {
      try {
        // tenant_id is injected server-side; don't pass it as a filter
        const result = await listResource('portfolio_companies', { fund_id: fundId, order: 'name', select: 'id, name' });
        setCompanies(result.data || []);
      } catch (err) {
        console.error('Failed to load companies:', err);
        setError('Failed to load portfolio companies. Please try again.');
        setCompanies([]);
      }
    };

    loadCompanies();
  }, [fundId, tenantId]);

  // Set default period to current quarter
  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const quarter = Math.floor(now.getMonth() / 3) + 1;
    setPeriod(`Q${quarter} ${year}`);
  }, []);

  const handleSubmit = async () => {
    if (!fundId || !portfolioCompanyId || !period.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (!data.file?.temp_storage_path) {
      setError('File upload data is missing. Please upload the file again.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // 1. Create submission record
      setStatus('Creating submission...');
      const submission = await createSubmission.mutateAsync({
        portfolio_company_id: portfolioCompanyId,
        period: period.trim(),
        source_type: 'upload',
        tenant_id: tenantId,
      });

      // 2. Move file from temp to permanent location
      setStatus('Moving file...');
      const workspace = await getResource<{ workspace_slug: string }>('tenants', tenantId, { select: 'workspace_slug' });

      if (!workspace) throw new Error('Failed to get workspace');

      const permanentPath = `${workspace.workspace_slug}/${portfolioCompanyId}/${submission.id}/${data.file.name}`;

      // Download from temp
      const tempFile = await downloadFileByKey(data.file.temp_storage_path);

      // Upload to permanent location in documents bucket
      const tempFileAsFile = new File([tempFile], data.file.name, { type: data.file.type });
      const { key: permanentKey } = await uploadFile('documents', permanentPath, tempFileAsFile);

      // 3. Create submission_thread record
      await createResource('submission_thread', {
        submission_id: submission.id,
        tenant_id: tenantId,
        file_name: data.file.name,
        file_path: permanentKey,
        file_size: data.file.size,
        mime_type: data.file.type,
        source_type: 'upload',
      });

      // 4. Clean up temp file
      try {
        await deleteFile(data.file.temp_storage_path);
      } catch {
        // Best effort — temp files will be cleaned up by retention policy
      }

      // 5. Trigger processing
      setStatus('Processing metrics...');
      const response = await authenticatedFetch('/agents/data-analyst/process', {
        method: 'POST',
        body: JSON.stringify({
          submissionId: submission.id,
          tenantId: tenantId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to process submission');
      }

      setStatus('Success! Processing your document...');

      // Get company name for message
      const company = companies.find((c) => c.id === portfolioCompanyId);
      const companyName = company?.name || 'company';

      setTimeout(() => {
        onSuccess({
          submissionId: submission.id,
          fundId,
          companyName,
          period: period.trim(),
        });
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to process upload');
      setIsProcessing(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Upload Document
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        File: {data.file.name} ({Math.round(data.file.size / 1024)} KB)
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {status && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {status}
        </Alert>
      )}

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Fund</InputLabel>
          <Select value={fundId} onChange={(e) => setFundId(e.target.value)} label="Fund" disabled={isProcessing}>
            {data.funds.map((fund) => (
              <MenuItem key={fund.id} value={fund.id}>
                {fund.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }} disabled={!fundId || isProcessing}>
          <InputLabel>Portfolio Company</InputLabel>
          <Select value={portfolioCompanyId} onChange={(e) => setPortfolioCompanyId(e.target.value)} label="Portfolio Company">
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField fullWidth label="Period" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="e.g., Q3 2024" disabled={isProcessing} sx={{ mb: 2 }} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button onClick={onClose} disabled={isProcessing} fullWidth variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isProcessing || !fundId || !portfolioCompanyId || !period.trim()} fullWidth variant="contained">
          {isProcessing ? <CircularProgress size={24} /> : 'Process Document'}
        </Button>
      </Box>
    </Box>
  );
}
